import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import DocumentOverviewCard from './DocumentOverviewCard';
import ExtractedDocumentPanel from './ExtractedDocumentPanel';

/**
 * ScanQualityReport is the top-level container component that renders
 * the full scan quality report for an uploaded document.
 *
 * It accepts the complete JSON output from the AI scan pipeline and
 * renders:
 * 1. A document overview card (summary of the upload)
 * 2. One ExtractedDocumentPanel per extracted/split document
 *
 * Usage:
 * ```jsx
 * import ScanQualityReport from './scanReport/ScanQualityReport';
 *
 * <ScanQualityReport data={scanResultJson} />
 * ```
 *
 * @param {Object} props
 * @param {Object} props.data - The full scan pipeline output JSON
 * @param {boolean} [props.showDetailedChecks] - Whether to allow expanding individual quality check lists (default false)
 * @param {Object} [props.sx] - Additional MUI sx styles for the outer container
 */
const ScanQualityReport = ({ data, showDetailedChecks = false, sx = {} }) => {
  if (!data) {
    return (
      <Alert severity="info">No scan data available.</Alert>
    );
  }

  const {
    main_document_details: details,
    main_document_summary: summary,
    main_document_warnings: warnings,
    extracted_document_quality_reports: extractedReports,
  } = data;

  // Sort extracted documents by doc_number (the keys are string numbers)
  const sortedDocKeys = Object.keys(extractedReports || {}).sort(
    (a, b) => Number(a) - Number(b)
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', ...sx }}>
      {/* Page title */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Document Scan Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This report details the automated analysis of the uploaded document,
        including document identification, data extraction quality checks,
        and cross-validation results.
      </Typography>

      <Stack spacing={3}>
        {/* Document overview */}
        <DocumentOverviewCard
          details={details}
          summary={summary}
          warnings={warnings}
        />

        {/* Extracted documents */}
        {sortedDocKeys.length > 0 && (
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Extracted Documents ({sortedDocKeys.length})
            </Typography>

            <Stack spacing={2}>
              {sortedDocKeys.map((key) => {
                const report = extractedReports[key];
                const docNumber = report?.extracted_document_details?.doc_number || Number(key);

                return (
                  <ExtractedDocumentPanel
                    key={key}
                    docNumber={docNumber}
                    report={report}
                    showDetailedChecks={showDetailedChecks}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {sortedDocKeys.length === 0 && (
          <Alert severity="info" variant="outlined">
            No extracted documents were found in this upload.
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

ScanQualityReport.propTypes = {
  data: PropTypes.shape({
    main_document_details: PropTypes.object,
    main_document_summary: PropTypes.object,
    main_document_warnings: PropTypes.array,
    extracted_document_quality_reports: PropTypes.object,
  }),
  showDetailedChecks: PropTypes.bool,
  sx: PropTypes.object,
};

export default ScanQualityReport;